/**
 * Programmatic SEO Data Engine
 * Generates 2000+ long-tail landing page configs from keyword matrix cross-products.
 */
import { buildCanonicalUrl } from "./siteSeo.js";

// ---------------------------------------------------------------------------
// 1. Keyword Dimensions
// ---------------------------------------------------------------------------

interface ActionDef {
  key: string;
  label: string;
  verb: string;
  toolPath: string;
  toolLabel: string;
  category: "organize" | "convert" | "security" | "edit" | "extract" | "ai";
  supportsLanguage?: boolean;
  /** extra keywords to inject */
  extraKw?: string[];
}

const ACTIONS: ActionDef[] = [
  { key: "compress", label: "Compress PDF", verb: "compress", toolPath: "/tools/compress", toolLabel: "Open Compress PDF Tool", category: "organize", extraKw: ["reduce pdf size", "shrink pdf"] },
  { key: "merge", label: "Merge PDF", verb: "merge", toolPath: "/tools/merge", toolLabel: "Open Merge PDF Tool", category: "organize", extraKw: ["combine pdf", "join pdf files"] },
  { key: "split", label: "Split PDF", verb: "split", toolPath: "/tools/split", toolLabel: "Open Split PDF Tool", category: "organize", extraKw: ["separate pdf", "extract pdf pages"] },
  { key: "edit", label: "Edit PDF", verb: "edit", toolPath: "/tools/edit-pdf", toolLabel: "Open Edit PDF Tool", category: "edit", extraKw: ["modify pdf", "change pdf text"] },
  { key: "convert-to-word", label: "Convert PDF to Word", verb: "convert", toolPath: "/tools/convert/pdf-to-word", toolLabel: "Open PDF to Word Tool", category: "convert", extraKw: ["pdf to docx", "pdf to word"] },
  { key: "convert-to-image", label: "Convert PDF to Image", verb: "convert", toolPath: "/tools/convert/pdf-to-image", toolLabel: "Open PDF to Image Tool", category: "convert", extraKw: ["pdf to jpg", "pdf to png"] },
  { key: "convert-to-excel", label: "Convert PDF to Excel", verb: "convert", toolPath: "/tools/pdf-to-excel", toolLabel: "Open PDF to Excel Tool", category: "convert", extraKw: ["pdf to spreadsheet", "pdf table extraction"] },
  { key: "word-to-pdf", label: "Convert Word to PDF", verb: "convert", toolPath: "/tools/word-to-pdf", toolLabel: "Open Word to PDF Tool", category: "convert", extraKw: ["docx to pdf"] },
  { key: "image-to-pdf", label: "Convert Image to PDF", verb: "convert", toolPath: "/tools/convert/image-to-pdf", toolLabel: "Open Image to PDF Tool", category: "convert", extraKw: ["jpg to pdf", "png to pdf"] },
  { key: "sign", label: "Sign PDF", verb: "sign", toolPath: "/tools/esign-pdf", toolLabel: "Open eSign PDF Tool", category: "security", extraKw: ["esign pdf", "electronic signature pdf"] },
  { key: "encrypt", label: "Encrypt PDF", verb: "encrypt", toolPath: "/tools/encrypt", toolLabel: "Open Encrypt PDF Tool", category: "security", extraKw: ["password protect pdf", "lock pdf"] },
  { key: "decrypt", label: "Decrypt PDF", verb: "decrypt", toolPath: "/tools/decrypt", toolLabel: "Open Decrypt PDF Tool", category: "security", extraKw: ["unlock pdf", "remove pdf password"] },
  { key: "ocr", label: "OCR PDF", verb: "OCR", toolPath: "/tools/ocr-pdf", toolLabel: "Open OCR PDF Tool", category: "extract", supportsLanguage: true, extraKw: ["extract text from scanned pdf", "text recognition pdf"] },
  { key: "watermark", label: "Watermark PDF", verb: "watermark", toolPath: "/tools/watermark", toolLabel: "Open Watermark PDF Tool", category: "security", extraKw: ["add watermark to pdf"] },
  { key: "redact", label: "Redact PDF", verb: "redact", toolPath: "/tools/redact-text", toolLabel: "Open Redact PDF Tool", category: "security", extraKw: ["black out text in pdf", "censor pdf"] },
  { key: "crop", label: "Crop PDF", verb: "crop", toolPath: "/tools/crop", toolLabel: "Open Crop PDF Tool", category: "edit", extraKw: ["trim pdf margins", "resize pdf pages"] },
  { key: "flatten", label: "Flatten PDF", verb: "flatten", toolPath: "/tools/flatten-pdf", toolLabel: "Open Flatten PDF Tool", category: "edit", extraKw: ["flatten pdf forms", "remove pdf layers"] },
  { key: "remove-pages", label: "Remove Pages from PDF", verb: "remove pages from", toolPath: "/tools/remove-pages", toolLabel: "Open Remove Pages Tool", category: "organize", extraKw: ["delete pdf pages"] },
  { key: "add-page-numbers", label: "Add Page Numbers to PDF", verb: "add page numbers to", toolPath: "/tools/page-numbers", toolLabel: "Open Page Numbers Tool", category: "edit", extraKw: ["number pdf pages"] },
  { key: "annotate", label: "Annotate PDF", verb: "annotate", toolPath: "/tools/edit-pdf", toolLabel: "Open Edit PDF Tool", category: "edit", extraKw: ["highlight pdf", "add notes to pdf"] },
  { key: "compare", label: "Compare PDF", verb: "compare", toolPath: "/tools/pdf-compare", toolLabel: "Open PDF Compare Tool", category: "edit", extraKw: ["pdf diff", "pdf side by side"] },
  { key: "repair", label: "Repair PDF", verb: "repair", toolPath: "/tools/pdf-repair", toolLabel: "Open PDF Repair Tool", category: "edit", extraKw: ["fix corrupted pdf", "recover pdf"] },
  { key: "translate", label: "Translate PDF", verb: "translate", toolPath: "/tools/pdf-translator", toolLabel: "Open PDF Translator Tool", category: "ai", supportsLanguage: true, extraKw: ["pdf translation"] },
  { key: "summarize", label: "Summarize PDF", verb: "summarize", toolPath: "/tools/ai/summarize", toolLabel: "Open Summarize PDF Tool", category: "ai", extraKw: ["pdf summary", "tldr pdf"] },
  { key: "html-to-pdf", label: "Convert HTML to PDF", verb: "convert", toolPath: "/tools/html-to-pdf", toolLabel: "Open HTML to PDF Tool", category: "convert", extraKw: ["webpage to pdf", "save html as pdf"] },
];

interface ContextDef { key: string; label: string; preposition: string; }
const CONTEXTS: ContextDef[] = [
  { key: "for-email", label: "for Email", preposition: "for" },
  { key: "for-printing", label: "for Printing", preposition: "for" },
  { key: "for-court", label: "for Court", preposition: "for" },
  { key: "for-school", label: "for School", preposition: "for" },
  { key: "for-business", label: "for Business", preposition: "for" },
  { key: "for-lawyers", label: "for Lawyers", preposition: "for" },
  { key: "for-students", label: "for Students", preposition: "for" },
  { key: "for-teachers", label: "for Teachers", preposition: "for" },
  { key: "for-hr", label: "for HR", preposition: "for" },
  { key: "for-real-estate", label: "for Real Estate", preposition: "for" },
  { key: "for-healthcare", label: "for Healthcare", preposition: "for" },
  { key: "for-free", label: "for Free", preposition: "" },
  { key: "without-upload", label: "without Upload", preposition: "" },
  { key: "locally", label: "Locally", preposition: "" },
  { key: "without-software", label: "without Software", preposition: "" },
];

interface PlatformDef { key: string; label: string; }
const PLATFORMS: PlatformDef[] = [
  { key: "on-iphone", label: "on iPhone" },
  { key: "on-android", label: "on Android" },
  { key: "on-mac", label: "on Mac" },
  { key: "on-windows", label: "on Windows" },
  { key: "on-chromebook", label: "on Chromebook" },
  { key: "on-ipad", label: "on iPad" },
  { key: "on-linux", label: "on Linux" },
  { key: "in-chrome", label: "in Chrome" },
  { key: "in-browser", label: "in Browser" },
  { key: "online", label: "Online" },
];

interface LanguageDef { key: string; label: string; }
const LANGUAGES: LanguageDef[] = [
  { key: "in-hindi", label: "in Hindi" },
  { key: "in-spanish", label: "in Spanish" },
  { key: "in-french", label: "in French" },
  { key: "in-german", label: "in German" },
  { key: "in-chinese", label: "in Chinese" },
  { key: "in-japanese", label: "in Japanese" },
  { key: "in-arabic", label: "in Arabic" },
  { key: "in-portuguese", label: "in Portuguese" },
  { key: "in-korean", label: "in Korean" },
  { key: "in-italian", label: "in Italian" },
];

// ---------------------------------------------------------------------------
// 2. Types
// ---------------------------------------------------------------------------

export interface ProgrammaticPage {
  slug: string;
  path: string;
  title: string;
  metaDescription: string;
  keywords: string;
  h1: string;
  toolPath: string;
  toolLabel: string;
  paragraphs: string[];
  steps: { title: string; description: string }[];
  faqs: { question: string; answer: string }[];
  relatedPages: { label: string; href: string }[];
  jsonLd: Record<string, unknown>[];
  action: string;
  context?: string;
  platform?: string;
  language?: string;
}

// ---------------------------------------------------------------------------
// 3. Smart Filtering — skip nonsensical combos
// ---------------------------------------------------------------------------

const BLOCKED_COMBOS = new Set([
  // Encrypt/decrypt don't make sense with language
  "encrypt+language", "decrypt+language",
  // Watermark doesn't pair with language
  "watermark+language",
  // Flatten doesn't pair with language
  "flatten+language",
  // Remove pages doesn't pair with language
  "remove-pages+language",
  // Add page numbers doesn't pair with language
  "add-page-numbers+language",
  // Compare doesn't pair with language
  "compare+language",
  // Repair doesn't pair with language
  "repair+language",
  // Crop doesn't pair with language
  "crop+language",
]);

function isValidCombo(
  action: ActionDef,
  context?: ContextDef,
  platform?: PlatformDef,
  language?: LanguageDef,
): boolean {
  // Language only for OCR / translate
  if (language && !action.supportsLanguage) return false;
  // Block specific combos
  if (language && BLOCKED_COMBOS.has(`${action.key}+language`)) return false;
  // Don't combine context + platform + language (too long / thin)
  if (context && platform && language) return false;
  // "for free" + "without upload" is redundant with "locally"
  if (context?.key === "for-free" && platform?.key === "in-browser") return false;
  if (context?.key === "without-upload" && platform?.key === "in-browser") return false;
  if (context?.key === "locally" && platform?.key === "in-browser") return false;
  return true;
}

// ---------------------------------------------------------------------------
// 4. Content Templates
// ---------------------------------------------------------------------------

function buildSlug(action: ActionDef, ctx?: ContextDef, plat?: PlatformDef, lang?: LanguageDef): string {
  let s = action.key;
  if (action.key.includes("-to-")) s = action.key; // "convert-to-word" etc
  else s = `${action.key}-pdf`;
  if (ctx) s += `-${ctx.key}`;
  if (plat) s += `-${plat.key}`;
  if (lang) s += `-${lang.key}`;
  return s;
}

function buildH1(action: ActionDef, ctx?: ContextDef, plat?: PlatformDef, lang?: LanguageDef): string {
  let h = `How to ${action.label}`;
  if (lang) h += ` ${lang.label}`;
  if (ctx) h += ` ${ctx.label}`;
  if (plat) h += ` ${plat.label}`;
  return h;
}

function buildTitle(h1: string): string {
  return `${h1} | Free & Private | PDFWorkSpace`;
}

function buildMetaDesc(action: ActionDef, ctx?: ContextDef, plat?: PlatformDef, lang?: LanguageDef): string {
  let d = `${action.label}`;
  if (lang) d += ` ${lang.label}`;
  if (ctx) d += ` ${ctx.label}`;
  if (plat) d += ` ${plat.label}`;
  d += ` — free, private, no upload required. Use PDFWorkSpace's browser-based tool to ${action.verb} your PDF in seconds.`;
  return d;
}

function buildKeywords(action: ActionDef, ctx?: ContextDef, plat?: PlatformDef, lang?: LanguageDef): string {
  const kws: string[] = [
    action.label.toLowerCase(),
    ...(action.extraKw || []),
  ];
  if (ctx) kws.push(`${action.label.toLowerCase()} ${ctx.label.toLowerCase()}`);
  if (plat) kws.push(`${action.label.toLowerCase()} ${plat.label.toLowerCase()}`);
  if (lang) kws.push(`${action.label.toLowerCase()} ${lang.label.toLowerCase()}`);
  kws.push("pdf tools", "no upload pdf", "privacy first pdf");
  return [...new Set(kws)].join(", ");
}

function buildParagraphs(action: ActionDef, ctx?: ContextDef, plat?: PlatformDef, lang?: LanguageDef): string[] {
  const actionName = action.label.toLowerCase();
  const contextPhrase = ctx ? ` ${ctx.label.toLowerCase()}` : "";
  const platformPhrase = plat ? ` ${plat.label.toLowerCase()}` : "";
  const langPhrase = lang ? ` ${lang.label.toLowerCase()}` : "";
  const qualifier = `${langPhrase}${contextPhrase}${platformPhrase}`;

  const p1Map: Record<string, string> = {
    organize: `Need to ${actionName}${qualifier}? PDFWorkSpace lets you ${action.verb} PDF documents entirely in your browser without uploading files to any server. Your documents stay on your device throughout the entire process, making it ideal for sensitive${contextPhrase || " personal"} documents.`,
    convert: `Looking to ${actionName}${qualifier}? This browser-based tool handles the conversion locally on your device — no server uploads, no waiting for cloud processing. The result downloads directly to your device the moment conversion finishes.`,
    security: `${action.label}${qualifier} with a privacy-first workflow that never sends your document to a remote server. PDFWorkSpace processes everything in the browser so your sensitive files remain completely under your control.`,
    edit: `${action.label}${qualifier} using a visual browser-based editor. No software installation required — open the tool, upload your PDF, make changes, and download the result. Everything happens locally on your device.`,
    extract: `${action.label}${qualifier} with on-device processing that keeps your documents private. The extraction engine runs entirely in your browser${langPhrase ? `, with support for ${lang!.label} text recognition` : ""}.`,
    ai: `${action.label}${qualifier} using AI-assisted analysis that processes your document content locally. Get ${action.key === "summarize" ? "concise summaries" : "accurate translations"} without exposing your PDF to third-party cloud services.`,
  };

  const p2Map: Record<string, string> = {
    organize: `This workflow is designed for users who need quick, reliable PDF ${action.key} functionality${contextPhrase ? ` in ${ctx!.label.toLowerCase()} workflows` : ""}. Whether you are handling contracts, reports, invoices, or study materials, the tool processes your files in seconds and produces a clean output ready for sharing or archiving.`,
    convert: `The conversion preserves text, basic formatting, and document structure while keeping the process completely local. This makes it particularly useful${contextPhrase ? ` ${ctx!.label.toLowerCase()}` : ""} when you need to move content between formats without compromising document privacy or waiting for server-side processing.`,
    security: `Whether you are preparing documents for${contextPhrase || " external sharing"}, this tool ensures your security workflow stays local. No intermediary server sees your content, passwords, or permission settings during the process.`,
    edit: `The editor supports common PDF modifications including text overlay, annotations, and layout adjustments${platformPhrase ? ` — all accessible ${plat!.label.toLowerCase()}` : ""}. Changes are applied in real time so you can preview the result before downloading.`,
    extract: `The extraction process works on both text-based and scanned PDFs${langPhrase ? ` with ${lang!.label} language support` : ""}. Results are available for download immediately after processing, with no cloud dependency or account required.`,
    ai: `The analysis runs on your document content after it is loaded into the browser. Results are generated locally${langPhrase ? ` with ${lang!.label} language understanding` : ""}, making this tool suitable for confidential materials that cannot be sent to external APIs.`,
  };

  const p3 = `After completing this task, explore related PDF tools like ${
    action.category === "organize" ? "compressing, converting, or securing" :
    action.category === "convert" ? "merging, splitting, or compressing" :
    action.category === "security" ? "merging, editing, or converting" :
    "compressing, merging, or converting"
  } your documents — all available in the same browser-based, privacy-first workspace. No account needed, no data collected.`;

  return [
    p1Map[action.category] || p1Map.organize,
    p2Map[action.category] || p2Map.organize,
    p3,
  ];
}

function buildSteps(action: ActionDef, _ctx?: ContextDef, plat?: PlatformDef): { title: string; description: string }[] {
  const platformNote = plat ? ` ${plat.label.toLowerCase()}` : "";
  return [
    {
      title: "Open the Tool",
      description: `Open the ${action.label} tool in your browser${platformNote}. No download or installation required — it works directly in any modern browser.`,
    },
    {
      title: "Upload Your PDF",
      description: `Select the PDF file you want to ${action.verb}. The file stays on your device and is processed locally in the browser — nothing is uploaded to a server.`,
    },
    {
      title: "Download the Result",
      description: `Review the output, then download the processed PDF directly to your device. The entire workflow takes just a few seconds.`,
    },
  ];
}

function buildFaqs(action: ActionDef, ctx?: ContextDef, plat?: PlatformDef, lang?: LanguageDef): { question: string; answer: string }[] {
  const qualifier = [lang?.label, ctx?.label, plat?.label].filter(Boolean).join(" ");
  const fullLabel = qualifier ? `${action.label} ${qualifier}` : action.label;

  return [
    {
      question: `Is it free to ${fullLabel.toLowerCase()}?`,
      answer: `Yes. PDFWorkSpace offers this tool for free with no account required. The tool runs entirely in your browser with no hidden server processing.`,
    },
    {
      question: `Is my PDF uploaded to a server when I ${action.verb} it?`,
      answer: `No. Your PDF is processed locally in your browser. The file never leaves your device, making this workflow safe for sensitive, confidential, and private documents.`,
    },
    {
      question: `Can I ${fullLabel.toLowerCase()} on my phone?`,
      answer: `Yes. PDFWorkSpace works in any modern mobile browser including Safari on iPhone, Chrome on Android, and other mobile browsers. No app installation is needed.`,
    },
    {
      question: `What PDF file size is supported?`,
      answer: `The tool handles PDFs up to several hundred megabytes depending on your device's available memory. For very large files, consider splitting them first.`,
    },
    {
      question: `What should I do after I ${action.verb} my PDF?`,
      answer: `Common next steps include compressing the result for sharing, merging it with other documents, adding page numbers, or converting to another format. All these tools are available in PDFWorkSpace.`,
    },
  ];
}

// ---------------------------------------------------------------------------
// 5. Page Generator
// ---------------------------------------------------------------------------

function createPage(
  action: ActionDef,
  ctx?: ContextDef,
  plat?: PlatformDef,
  lang?: LanguageDef,
): ProgrammaticPage {
  const slug = buildSlug(action, ctx, plat, lang);
  const path = `/how-to/${slug}`;
  const h1 = buildH1(action, ctx, plat, lang);

  return {
    slug,
    path,
    title: buildTitle(h1),
    metaDescription: buildMetaDesc(action, ctx, plat, lang),
    keywords: buildKeywords(action, ctx, plat, lang),
    h1,
    toolPath: action.toolPath,
    toolLabel: action.toolLabel,
    paragraphs: buildParagraphs(action, ctx, plat, lang),
    steps: buildSteps(action, ctx, plat),
    faqs: buildFaqs(action, ctx, plat, lang),
    relatedPages: [], // filled in linking pass
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: h1,
        description: buildMetaDesc(action, ctx, plat, lang),
        url: buildCanonicalUrl(path),
        step: buildSteps(action, ctx, plat).map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.title,
          text: s.description,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: `PDFWorkSpace — ${action.label}`,
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        url: buildCanonicalUrl(action.toolPath),
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: buildFaqs(action, ctx, plat, lang).map(f => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      },
    ],
    action: action.key,
    context: ctx?.key,
    platform: plat?.key,
    language: lang?.key,
  };
}

function generateAllPages(): ProgrammaticPage[] {
  const pages: ProgrammaticPage[] = [];
  const slugSet = new Set<string>();

  const addPage = (p: ProgrammaticPage) => {
    if (slugSet.has(p.slug)) return;
    slugSet.add(p.slug);
    pages.push(p);
  };

  for (const action of ACTIONS) {
    // Action only: "compress-pdf"
    addPage(createPage(action));

    // Action + Context: "compress-pdf-for-email"
    for (const ctx of CONTEXTS) {
      if (isValidCombo(action, ctx)) addPage(createPage(action, ctx));
    }

    // Action + Platform: "compress-pdf-on-iphone"
    for (const plat of PLATFORMS) {
      if (isValidCombo(action, undefined, plat)) addPage(createPage(action, undefined, plat));
    }

    // Action + Context + Platform: "compress-pdf-for-email-on-iphone"
    for (const ctx of CONTEXTS) {
      for (const plat of PLATFORMS) {
        if (isValidCombo(action, ctx, plat)) addPage(createPage(action, ctx, plat));
      }
    }

    // Action + Language (only OCR/translate): "ocr-pdf-in-hindi"
    if (action.supportsLanguage) {
      for (const lang of LANGUAGES) {
        if (isValidCombo(action, undefined, undefined, lang)) addPage(createPage(action, undefined, undefined, lang));
      }
      // Action + Language + Context
      for (const lang of LANGUAGES) {
        for (const ctx of CONTEXTS) {
          if (isValidCombo(action, ctx, undefined, lang)) addPage(createPage(action, ctx, undefined, lang));
        }
      }
      // Action + Language + Platform
      for (const lang of LANGUAGES) {
        for (const plat of PLATFORMS) {
          if (isValidCombo(action, undefined, plat, lang)) addPage(createPage(action, undefined, plat, lang));
        }
      }
    }
  }

  return pages;
}

// ---------------------------------------------------------------------------
// 6. Build pages + cross-link
// ---------------------------------------------------------------------------

const allPages = generateAllPages();

// Internal linking: each page links to 4 related pages
for (const page of allPages) {
  const related = allPages
    .filter(p => p.slug !== page.slug)
    .filter(p => p.action === page.action || p.context === page.context || p.platform === page.platform)
    .slice(0, 4)
    .map(p => ({ label: p.h1.replace("How to ", ""), href: p.path }));

  if (related.length < 4) {
    const extra = allPages
      .filter(p => p.slug !== page.slug && !related.some(r => r.href === p.path))
      .slice(0, 4 - related.length)
      .map(p => ({ label: p.h1.replace("How to ", ""), href: p.path }));
    related.push(...extra);
  }

  page.relatedPages = related;
}

// ---------------------------------------------------------------------------
// 7. Exports
// ---------------------------------------------------------------------------

export const PROGRAMMATIC_SEO_PAGES = allPages;

export const PROGRAMMATIC_SEO_MAP = new Map<string, ProgrammaticPage>(
  allPages.map(p => [p.slug, p]),
);

export const PROGRAMMATIC_SEO_PATHS = allPages.map(p => p.path);

/** Top pages for featuring on homepage / footer */
export const FEATURED_PROGRAMMATIC_PAGES = [
  "compress-pdf-for-email",
  "merge-pdf-on-iphone",
  "ocr-pdf-in-hindi",
  "split-pdf-for-lawyers",
  "convert-to-word-on-mac",
  "sign-pdf-for-business",
  "encrypt-pdf-for-healthcare",
  "compress-pdf-on-android",
  "edit-pdf-for-students",
  "summarize-pdf-for-school",
].filter(slug => PROGRAMMATIC_SEO_MAP.has(slug));
